import React from 'react';
import { motion } from 'framer-motion';
import { useAdminStore } from '@/lib/admin-store';

/* ── Platform Icons ── */

const instagramIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const facebookIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const behanceIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M7.5 11c1.38 0 2.5-1.12 2.5-2.5S8.88 6 7.5 6H3v5h4.5zm0 2H3v5h4.5c1.38 0 2.5-1.12 2.5-2.5S8.88 13 7.5 13zM21 6.5a3.5 3.5 0 0 0-7 0v1a3.5 3.5 0 0 0 5.95 2.5H21v-1h-3.05A3.5 3.5 0 0 0 21 6.5zM17.5 5a1.5 1.5 0 0 1 1.45 1.12H16.05A1.5 1.5 0 0 1 17.5 5zM14 13.5a3.5 3.5 0 1 0 7 0v-1h-7v1zm1.95-.5h3.1a1.5 1.5 0 0 1-3.1 0z" />
  </svg>
);

const pinterestIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.236 2.636 7.855 6.356 9.312-.088-.791-.167-2.005.035-2.868.182-.78 1.172-4.97 1.172-4.97s-.299-.6-.299-1.486c0-1.39.806-2.428 1.81-2.428.852 0 1.264.64 1.264 1.408 0 .858-.546 2.14-.828 3.33-.236.995.5 1.807 1.48 1.807 1.778 0 3.144-1.874 3.144-4.58 0-2.393-1.72-4.068-4.177-4.068-2.845 0-4.515 2.135-4.515 4.34 0 .859.331 1.781.745 2.281a.3.3 0 0 1 .069.288l-.278 1.133c-.044.183-.145.222-.335.134-1.249-.581-2.03-2.407-2.03-3.874 0-3.154 2.292-6.052 6.608-6.052 3.469 0 6.165 2.473 6.165 5.776 0 3.447-2.173 6.22-5.19 6.22-1.013 0-1.965-.527-2.291-1.148l-.623 2.378c-.226.869-.835 1.958-1.244 2.621.937.29 1.931.446 2.962.446 5.523 0 10-4.477 10-10S17.523 2 12 2z" />
  </svg>
);

const tiktokIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78c.29 0 .58.04.84.11v-3.5a6.37 6.37 0 0 0-.84-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.89a8.17 8.17 0 0 0 3.76.92V6.69z" />
  </svg>
);

const youtubeIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.81 3.02 3.02 0 0 0 2.12 2.14c1.88.55 9.38.55 9.38.55s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
  </svg>
);

const xIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const linkedinIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const vimeoIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.523 3.01 7.523c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z" />
  </svg>
);

const dribbbleIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[22px] h-[22px]">
    <path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-3.17-.953-6.384-.438 1.34 3.684 1.887 6.684 1.992 7.308 2.3-1.555 3.936-4.02 4.395-6.87zm-6.115 7.808c-.153-.9-.75-4.032-2.19-7.77l-.066.02c-5.79 2.015-7.86 6.025-8.04 6.4 1.73 1.358 3.92 2.166 6.29 2.166 1.42 0 2.77-.29 4-.81zm-11.62-2.58c.232-.4 3.045-5.055 8.332-6.765.135-.045.27-.084.405-.12-.26-.585-.54-1.167-.832-1.74C7.17 11.775 2.206 11.71 1.756 11.7l-.004.312c0 2.633.998 5.037 2.634 6.855zm-2.42-8.955c.46.008 4.683.026 9.477-1.248-1.698-3.018-3.53-5.558-3.8-5.928-2.868 1.35-5.01 3.99-5.676 7.17zM9.6 2.052c.282.38 2.145 2.914 3.822 6 3.645-1.365 5.19-3.44 5.373-3.702A9.63 9.63 0 0 0 12 1.8c-.817 0-1.615.088-2.4.252zM20.063 5.7c-.215.285-1.935 2.504-5.744 4.024.241.49.47.985.68 1.486.075.18.15.36.22.53 3.41-.43 6.8.26 7.14.33-.02-2.42-.88-4.64-2.3-6.37z" />
  </svg>
);

const linkIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

/* ── Platform auto-detection ── */

const PLATFORMS: Array<{ match: string; name: string; icon: React.ReactNode }> = [
  { match: 'instagram.com', name: 'Instagram', icon: instagramIcon },
  { match: 'facebook.com', name: 'Facebook', icon: facebookIcon },
  { match: 'behance.net', name: 'Behance', icon: behanceIcon },
  { match: 'pinterest.com', name: 'Pinterest', icon: pinterestIcon },
  { match: 'tiktok.com', name: 'TikTok', icon: tiktokIcon },
  { match: 'youtube.com', name: 'YouTube', icon: youtubeIcon },
  { match: 'youtu.be', name: 'YouTube', icon: youtubeIcon },
  { match: 'twitter.com', name: 'X', icon: xIcon },
  { match: 'x.com', name: 'X', icon: xIcon },
  { match: 'linkedin.com', name: 'LinkedIn', icon: linkedinIcon },
  { match: 'vimeo.com', name: 'Vimeo', icon: vimeoIcon },
  { match: 'dribbble.com', name: 'Dribbble', icon: dribbbleIcon },
];

export function detectPlatform(url: string): { name: string; icon: React.ReactNode } {
  const lower = url.toLowerCase();
  for (const p of PLATFORMS) {
    if (lower.includes(p.match)) return { name: p.name, icon: p.icon };
  }
  return { name: 'Link', icon: linkIcon };
}

/* ── Animation config ── */

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 1.0,
    },
  },
};

const item = {
  hidden: { opacity: 0, x: 30 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] },
  },
};

/* ── Keys in the order they're stored ── */
const SLOT_KEYS = ['instagram', 'facebook', 'behance', 'pinterest'] as const;

const SocialSidebar: React.FC = () => {
  const socialLinksArray = useAdminStore((s) => s.settings?.socialLinksArray);
  const socialLinksOld = useAdminStore((s) => s.settings?.socialLinks);

  // Use new array format if available, fallback to old object format
  const visibleSocials = React.useMemo(() => {
    if (socialLinksArray && socialLinksArray.length > 0) {
      return socialLinksArray
        .filter((url) => url && url.trim().length > 0)
        .map((url) => {
          const detected = detectPlatform(url);
          return { url, name: detected.name, icon: detected.icon };
        });
    }
    // Fallback to old format
    return SLOT_KEYS
      .map((key) => {
        const url = socialLinksOld?.[key];
        if (!url || url.trim().length === 0) return null;
        const detected = detectPlatform(url);
        return { url, name: detected.name, icon: detected.icon };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [socialLinksArray, socialLinksOld]);

  if (visibleSocials.length === 0) return null;

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="fixed right-6 top-1/2 -translate-y-1/2 z-[100] hidden lg:flex flex-col items-center gap-1.5"
    >
      {/* Vertical line top */}
      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 0.8, delay: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="w-px h-14 bg-black/15 dark:bg-white/15 origin-top mb-2"
      />

      {visibleSocials.map((social) => (
        <motion.a
          key={social.url}
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          variants={item}
          whileHover={{ scale: 1.15, x: -5 }}
          whileTap={{ scale: 0.9 }}
          className="group relative w-12 h-12 flex items-center justify-center text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-all duration-300"
          aria-label={social.name}
        >
          {/* Background glow on hover */}
          <span className="absolute inset-1 rounded-xl bg-black/0 dark:bg-white/0 group-hover:bg-black/[0.06] dark:group-hover:bg-white/[0.08] transition-all duration-300" />

          <span className="relative z-10">{social.icon}</span>

          {/* Tooltip */}
          <span className="absolute right-full mr-4 px-3 py-1.5 bg-black dark:bg-white text-white dark:text-black text-[9px] font-bold uppercase tracking-[0.15em] rounded-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-lg">
            {social.name}
            {/* Arrow */}
            <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-[3px] w-1.5 h-1.5 bg-black dark:bg-white rotate-45" />
          </span>

          {/* Hover ring */}
          <span className="absolute inset-0.5 rounded-xl border border-black/0 dark:border-white/0 group-hover:border-black/10 dark:group-hover:border-white/10 transition-all duration-300" />
        </motion.a>
      ))}

      {/* Vertical line bottom */}
      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 0.8, delay: 1.4, ease: [0.76, 0, 0.24, 1] }}
        className="w-px h-14 bg-black/15 dark:bg-white/15 origin-top mt-2"
      />
    </motion.div>
  );
};

export default SocialSidebar;
